package com.schoolconnect.app.feature.parentnotes

import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.datasource.network.response.ParentNoteReplyResponse
import com.schoolconnect.app.datasource.network.response.ParentNoteResponse

data class ParentNotesFilters(
    val student: String = "",
    val studentId: String = "",
    val classSectionId: String = "",
    val status: String = ""
)

data class ParentNoteRowUi(
    val backendId: String,
    val parentNoteId: String,
    val studentName: String,
    val className: String,
    val section: String,
    val title: String,
    val date: String,
    val status: String,
    val detail: String,
    val attachmentFileIds: List<String> = emptyList(),
    val teacherName: String = "—",
    val studentId: String = "",
    val teacherId: String = "",
    val conversationCode: String = ""
)

data class ParentNoteClassOption(val id: String, val name: String)

data class ParentNoteClassSectionOption(val id: String, val className: String, val sectionName: String) {
    val label: String get() = "$className-$sectionName"
}

internal fun parentNoteClassSectionComparator(): Comparator<ParentNoteClassSectionOption> =
    compareBy(
        { Regex("^\\s*(\\d+)").find(it.className.trim().ifBlank { it.label.trim() })?.groupValues?.getOrNull(1)?.toIntOrNull() ?: Int.MAX_VALUE },
        { it.sectionName.trim().uppercase() },
        { it.label.trim().uppercase() }
    )

data class ParentNoteSectionOption(val className: String, val name: String)

data class ParentNoteStudentOption(
    val id: String,
    val name: String,
    val className: String,
    val sectionName: String,
    val rollNumber: String
) {
    val displayLabel: String
        get() = buildString {
            append(name)
            val classSection = listOf(className, sectionName).filter(String::isNotBlank).joinToString("-")
            if (classSection.isNotBlank()) append(" • ").append(classSection)
            if (rollNumber.isNotBlank()) append(" • #").append(rollNumber)
        }
}

data class ParentNoteStudentContact(
    val studentId: String,
    val parentId: String,
    val rollNumber: String,
    val parentName: String,
    val mobile: String
)

data class ParentConversationInfo(
    val teacherName: String = "",
    val parentName: String = "",
    val mobile: String = "",
    val lastActivity: String = "",
    val conversationId: String = ""
)

data class ParentNoteDraft(
    val studentId: String,
    val parentId: String,
    val title: String,
    val message: String,
    val attachmentFileIds: List<String> = emptyList()
)

data class ParentNoteEditDraft(
    val parentNoteId: String,
    val title: String,
    val message: String,
    val attachmentFileIds: List<String> = emptyList()
)

data class ParentNotesUiState(
    val isLoading: Boolean = true,
    val isStatisticsLoading: Boolean = true,
    val errorMessage: String = "",
    val searchQuery: String = "",
    val filters: ParentNotesFilters = ParentNotesFilters(),
    val rows: List<ParentNoteRowUi> = emptyList(),
    val pageIndex: Int = 0,
    val pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
    val sortField: ParentNotesSortField = ParentNotesSortField.DATE,
    val sortAscending: Boolean = false,
    val totalCount: Int = 0,
    val totalNotesValue: Int = 0,
    val unreadValue: Int = 0,
    val pendingParentValue: Int = 0,
    val pendingTeacherValue: Int = 0,
    val teacherName: String = "",
    val pickerLoading: Boolean = true,
    val pickerError: String = "",
    val classPickerOptions: List<ParentNoteClassOption> = emptyList(),
    val classSectionPickerOptions: List<ParentNoteClassSectionOption> = emptyList(),
    val sectionPickerOptions: List<ParentNoteSectionOption> = emptyList(),
    val studentPickerOptions: List<ParentNoteStudentOption> = emptyList(),
    val studentPickerQuery: String = "",
    val studentPickerPageIndex: Int = 0,
    val studentPickerHasMore: Boolean = false,
    val studentPickerLoadingMore: Boolean = false,
    val selectedStudentContact: ParentNoteStudentContact? = null
    ,val viewedNote: ParentNoteResponse? = null
    ,val viewedReplies: List<ParentNoteReplyResponse> = emptyList()
    ,val viewedConversationInfo: ParentConversationInfo = ParentConversationInfo()
    ,val isViewingNote: Boolean = false
    ,val viewNoteError: String = ""
    ,val conversationCloseGeneration: Int = 0
    ,val replyResetGeneration: Int = 0
    ,val historyScrollGeneration: Int = 0
) {
    val totalNotes: Int get() = totalNotesValue
    val unreadCount: Int get() = unreadValue
    val pendingParentCount: Int get() = pendingParentValue
    val pendingTeacherCount: Int get() = pendingTeacherValue
    val classOptions: List<String> get() = classPickerOptions.map { it.name }.filter(String::isNotBlank).distinct()
    // The repository supplies this list already scoped by the selected class section.
    // Do not filter or sort the server result in the UI.
    val studentOptions: List<String> get() = studentPickerOptions.map { it.displayLabel }.filter(String::isNotBlank).distinct()
}

sealed interface ParentNotesUiEvent {
    data object RefreshClicked : ParentNotesUiEvent
    data object PickerOptionsRequested : ParentNotesUiEvent
    data class StudentPickerSearchChanged(val query: String) : ParentNotesUiEvent
    data object StudentPickerLoadMore : ParentNotesUiEvent
    data class StudentPrefilled(val student: ParentNoteStudentOption) : ParentNotesUiEvent
    data class StudentPickerScopeChanged(val classSectionId: String?) : ParentNotesUiEvent
    data class SearchChanged(val query: String) : ParentNotesUiEvent
    data class FiltersChanged(val filters: ParentNotesFilters) : ParentNotesUiEvent
    data object FiltersReset : ParentNotesUiEvent
    data class PageChanged(val pageIndex: Int) : ParentNotesUiEvent
    data class PageSizeChanged(val pageSize: Int) : ParentNotesUiEvent
    data class SortChanged(val field: ParentNotesSortField) : ParentNotesUiEvent
    data class StudentSelected(val studentId: String) : ParentNotesUiEvent
    data class CreateRequested(val draft: ParentNoteDraft) : ParentNotesUiEvent
    data class UpdateRequested(val draft: ParentNoteEditDraft) : ParentNotesUiEvent
    data class ReplyRequested(val parentNoteId: String, val message: String, val attachmentFileIds: List<String> = emptyList()) : ParentNotesUiEvent
    data class CloseRequested(val parentNoteId: String) : ParentNotesUiEvent
    data class ViewRequested(val parentNoteId: String) : ParentNotesUiEvent
    data class DeleteRequested(val parentNoteId: String) : ParentNotesUiEvent
    data object ViewDismissed : ParentNotesUiEvent
}

enum class ParentNotesSortField { ID, STUDENT, CLASS, TITLE, DATE, STATUS }
